import React, { useState, useMemo } from "react";
import debounce from "lodash/debounce";
import { CircularProgress, makeStyles, Modal } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";
import TranscriberService from "../../../../services/TranscriberService";
import PartyService from "../../../../services/PartyService";
import useLoader from "../../../../hooks/useLoader";
import {
  StyledAutocomplete,
  OptionContainer,
  OptionName,
  OptionEmail,
  OptionCheck,
  OptionContent,
} from "../../NoticeGeneration/component/CustomSelect";
import { CustomInputField } from "../../../common/FormInputs";
import { PrimaryCTAButton } from "../../../common/Buttons";
import styled from "styled-components";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import {
  ModalCard,
  ModalOuter,
  ModalHeader,
  HeaderGroup,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseBtn,
  Divider,
  FieldLabel,
} from "../../../Mis/component/Label";

const useStyles = makeStyles(() => ({
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    "& li[data-option-index]": {
      padding: "12px 16px !important",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "#f0f9ff",
        paddingLeft: "20px !important",
      },
    },
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
    padding: "16px",
    fontSize: "14px",
  },
  paper: {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
    borderRadius: "8px !important",
    border: "1px solid #e2e8f0 !important",
  },
}));

const PAGE_SIZE = 50;

const PartySearchAndTrigger = ({ modalOpen, setModalOpen, setLifeCycleModal, selectMenuItems }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const classes = useStyles();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [partySearchOpen, setPartySearchOpen] = useState(false);
  const [partyValue, setPartyValue] = useState(null);
  const [partyError, setPartyError] = useState("");

  const fetchParties = async (term, page) => {
    try {
      setLoading(true);
      const q = `?search=${encodeURIComponent(term)}&page=${page}&perPage=${PAGE_SIZE}&status=active`;
      const res = await PartyService.index(q);
      if (res?.data?.length) {
        const mapped = res.data.map(({ name, id, owner }) => ({
          label: name,
          value: id,
          email: owner?.email,
        }));
        setOptions((prev) => (page === 1 ? mapped : [...prev, ...mapped]));
        setTotal(res.meta?.total || total);
      }
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: "error" });
      if (err.response?.status >= 500) {
        setTimeout(() => fetchParties(term, page), 1000);
      }
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useMemo(
    () => debounce(fetchParties, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total],
  );

  const handleInputChange = (_, val) => {
    setInputValue(val);
    if (partySearchOpen && val.length >= 2) {
      setOptions([]);
      debouncedFetch(val, 1);
    } else if (!val) {
      setOptions([]);
    }
  };

  const handleModalClose = () => {
    setModalOpen({ state: false });
    setPartyValue(null);
    setInputValue("");
    setOptions([]);
    setPartyError("");
  };

  const handleTrigger = async () => {
    if (!partyValue) {
      setPartyError("Party is required");
      return;
    }
    setPartyError("");
    try {
      setLoader({ state: true, message: "Extracting..." });
      const data = await TranscriberService.extractCaseDetails({
        fileBase64: modalOpen?.metaData?.fileBase64,
        partyId: partyValue?.value,
      });
      if (data?.contractNo?.length > 0 || data?.caseIds?.length > 0) {
        handleModalClose();
        setLifeCycleModal({
          state: true,
          esignersId: modalOpen?.metaData?.id,
          esigner: true,
          contractNo: data?.contractNo,
          caseIdMenuItems: data?.caseIds,
          pdfFile: modalOpen?.metaData?.fileBase64,
        });
      } else {
        enqueueSnackbar("No caseId or contract number found", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(err?.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setModalOpen({ state: false });
    }
  };

  return (
    <>
      <Modal open={modalOpen?.state} onClose={handleModalClose}>
        <ModalOuter>
          <ModalCard>
            {/* Header */}
            <ModalHeader>
              <HeaderGroup>
                <ModalTitle>Select Party</ModalTitle>
              </HeaderGroup>
              <CloseBtn onClick={handleModalClose}>✕</CloseBtn>
            </ModalHeader>

            <Divider />

            {/* Form */}
            <ModalBody>
              <FieldLabel>
                Search Party <span className="required-star"> *</span>
              </FieldLabel>
              <StyledAutocomplete
                classes={{
                  listbox: classes.listbox,
                  noOptions: classes.noOptions,
                  paper: classes.paper,
                }}
                open={partySearchOpen}
                onOpen={() => setPartySearchOpen(true)}
                onClose={() => setPartySearchOpen(false)}
                value={partyValue}
                onChange={(_, val) => {
                  setPartyValue(val);
                  if (val) setPartyError("");
                }}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                selectOnFocus
                clearOnBlur
                options={options}
                loading={loading}
                noOptionsText={
                  inputValue.length < 2
                    ? "Type at least 2 characters to search"
                    : loading
                      ? "Searching…"
                      : "No parties found"
                }
                getOptionLabel={(o) => o.label?.toString() ?? ""}
                renderOption={(o) => (
                  <OptionContainer>
                    <OptionContent>
                      <OptionName>{o.label}</OptionName>
                      <OptionEmail>{o.email}</OptionEmail>
                    </OptionContent>
                    <OptionCheck>✓</OptionCheck>
                  </OptionContainer>
                )}
                renderInput={(params) => (
                  <CustomInputField
                    {...params}
                    variant="outlined"
                    placeholder="Type to search parties…"
                    error={Boolean(partyError)}
                    helperText={partyError}
                    onKeyDown={(e) => {
                      if (
                        (e.key === "Enter" || e.key === "Tab") &&
                        !partyValue
                      ) {
                        setPartyError("Party is required");
                        e.preventDefault();
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && (
                            <CircularProgress color="inherit" size={18} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Selected party preview */}
              {partyValue && (
                <SelectedPartyCard>
                  <SelectedLabel>Selected</SelectedLabel>
                  <SelectedName>{partyValue.label}</SelectedName>
                  <SelectedEmail>{partyValue.email}</SelectedEmail>
                </SelectedPartyCard>
              )}

              <ModalFooter>
                <PrimaryCTAButton
                  style={{ width: "auto", height: 35, borderRadius: 8 }}
                  disabled={!partyValue}
                  onClick={handleTrigger}
                >
                  Submit
                </PrimaryCTAButton>
              </ModalFooter>
            </ModalBody>
          </ModalCard>
        </ModalOuter>
      </Modal>

    </>
  );
};

export default PartySearchAndTrigger;

const SelectedPartyCard = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  background: #f4fdf9;
  border: 1px solid #b2dfce;
  border-radius: 8px;
`;

const SelectedLabel = styled.div`
  font-size: 11px;
  color: ${COLORS.BTN_GREEN};
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const SelectedName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
`;

const SelectedEmail = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  margin-top: 2px;
`;
